import { Prettify } from '@xemplo/types';

export type FormattableValue = Date | string | number | null | undefined;
export type FormatDateProps = {
  value?: FormattableValue;
  format?: string;
  /**
   * Parses the *input* as UTC — use it for backend values that are UTC instants,
   * including naive (`Z`-less) strings that would otherwise be read as local time.
   *
   * It does **not** render in UTC: the output is always converted to `tz`. To render
   * in UTC, pass `tz: 'UTC'`.
   *
   * @default false
   */
  isUtc?: boolean;
  /**
   * The timezone the output is rendered in. Only consumed when `isUtc` is `true`.
   *
   * @default the viewer's resolved timezone (`Australia/Sydney` when resolution fails)
   */
  tz?: string;
  fallback?: string;
};

export const FalsyPolicy = {
  Null: 'null',
  Undefined: 'undefined',
  Fallback: 'fallback',
} as const;
export type FalsyPolicy = (typeof FalsyPolicy)[keyof typeof FalsyPolicy];

/**
 * Properties for configuring the DateHelper instance.
 * Extends FormatDateProps by adding a falsyPolicy to handle nullish values.
 *
 * @property format - The date format string (default: 'DD/MM/YYYY').
 * @property isUtc - Whether to parse the input as UTC; output still renders in `tz` (default: false).
 * @property tz - The timezone the output renders in (default: browser's timezone, or `Australia/Sydney` when SSR).
 * @property fallback - The fallback string for invalid dates (default: '').
 * @property falsyPolicy - Policy for handling falsy values (default: 'fallback').
 */
export type DateHelperProps = Omit<FormatDateProps, 'value'> & {
  /**
   * Determines how falsy values are handled.
   * @default 'fallback'
   */
  falsyPolicy?: FalsyPolicy;

  /**
   * By default, we assume the input value is of standard date format.
   * However, there are cases where you know the input is something else (e.g., 'DD-MM-YYYY').
   * This setting allows specifying a custom input format to correctly parse such values.
   *
   * @default null
   */
  customInputFormat?: string | undefined;
};

/**
 * Normalized and required version of DateHelperProps.
 * All properties are required except `customInputFormat`, which remains optional.
 */
export type NormalizedDateHelperProps = Prettify<
  Omit<Required<DateHelperProps>, 'customInputFormat'> & {
    customInputFormat?: string;
  }
>;

/** @deprecated use `DateTimeFormat` instead */
export enum DisplayDateFormat {
  Default = 'DD/MM/YYYY',
  Short = 'D/M/YY',
  Medium = 'DD MMM YYYY',
}

/** @deprecated use `DateTimeFormat` instead */
export const TimeFormats = {
  HourMinute24Format: 'H:mm',
  HourMinute12Format: 'h:mm A',
} as const;
export type TimeFormats = (typeof TimeFormats)[keyof typeof TimeFormats];
